/**
 * Keyboard shortcut catalog (v1).
 * Paths / permissions align with Spotlight actionCatalog — keep in sync when routes change.
 */

export type ShortcutGroupId = 'search' | 'navigate' | 'create';

/** Chord: modifier combo or two-key sequence (G→A). */
export type ShortcutChord =
  | { kind: 'mod'; key: string }
  | { kind: 'sequence'; keys: readonly [string, string] };

export type ShortcutRun =
  | { type: 'open-spotlight' }
  | { type: 'open-help' }
  | { type: 'navigate'; path: string; state?: object }
  /** In-place create via CreateDialogHost (Spotlight action id). */
  | { type: 'open-create'; actionId: string; state?: object; path?: string };

/** i18n keys under `common` for shortcut labels / section titles. */
export type ShortcutLabelKey =
  | 'keyboardShortcuts'
  | 'globalSearch'
  | 'shortcutGoAppointments'
  | 'shortcutGoPatients'
  | 'shortcutGoDashboard'
  | 'shortcutGoMyTasks'
  | 'shortcutGoLeads'
  | 'shortcutGoMessaging'
  | 'spotlightCreateAppointment'
  | 'spotlightCreatePatient'
  | 'spotlightCreateLead'
  | 'spotlightCreateTask';

/** i18n keys for sequence mnemonics (`<mark>G</mark>o to <mark>A</mark>…`). */
export type ShortcutMnemonicKey =
  | 'shortcutMnemonicGoAppointments'
  | 'shortcutMnemonicGoPatients'
  | 'shortcutMnemonicGoDashboard'
  | 'shortcutMnemonicGoMyTasks'
  | 'shortcutMnemonicGoLeads'
  | 'shortcutMnemonicGoMessaging'
  | 'shortcutMnemonicCreateAppointment'
  | 'shortcutMnemonicCreatePatient'
  | 'shortcutMnemonicCreateLead'
  | 'shortcutMnemonicCreateTask';

export type ShortcutGroupLabelKey =
  | 'shortcutGroupSearch'
  | 'shortcutGroupNavigate'
  | 'shortcutGroupCreate';

export type ShortcutDef = {
  id: string;
  group: ShortcutGroupId;
  labelKey: ShortcutLabelKey;
  /**
   * Optional memory aid under the label (sequence chords).
   * Copy uses `<mark>…</mark>` around the letters that match the keys.
   */
  mnemonicKey?: ShortcutMnemonicKey;
  chord: ShortcutChord;
  run: ShortcutRun;
  /** Require this permission (admin bypasses via usePermission). */
  permission?: string;
};

export const SHORTCUT_GROUP_ORDER: ShortcutGroupId[] = [
  'search',
  'navigate',
  'create',
];

export const SHORTCUT_GROUP_LABEL: Record<ShortcutGroupId, ShortcutGroupLabelKey> = {
  search: 'shortcutGroupSearch',
  navigate: 'shortcutGroupNavigate',
  create: 'shortcutGroupCreate',
};

/** Sequence chord window (ms) after the first key. */
export const SHORTCUT_SEQUENCE_MS = 1000;

export const KEYBOARD_SHORTCUTS: ShortcutDef[] = [
  {
    id: 'open-spotlight',
    group: 'search',
    labelKey: 'globalSearch',
    chord: { kind: 'mod', key: 'k' },
    run: { type: 'open-spotlight' },
  },
  {
    id: 'open-help',
    group: 'search',
    labelKey: 'keyboardShortcuts',
    chord: { kind: 'mod', key: '/' },
    run: { type: 'open-help' },
  },
  {
    id: 'go-appointments',
    group: 'navigate',
    labelKey: 'shortcutGoAppointments',
    mnemonicKey: 'shortcutMnemonicGoAppointments',
    chord: { kind: 'sequence', keys: ['g', 'a'] },
    run: { type: 'navigate', path: '/appointments?view=calendar' },
    permission: 'appointments:read',
  },
  {
    id: 'go-patients',
    group: 'navigate',
    labelKey: 'shortcutGoPatients',
    mnemonicKey: 'shortcutMnemonicGoPatients',
    chord: { kind: 'sequence', keys: ['g', 'p'] },
    run: { type: 'navigate', path: '/patients' },
    permission: 'patients:read',
  },
  {
    id: 'go-leads',
    group: 'navigate',
    labelKey: 'shortcutGoLeads',
    mnemonicKey: 'shortcutMnemonicGoLeads',
    chord: { kind: 'sequence', keys: ['g', 'l'] },
    run: { type: 'navigate', path: '/leads' },
    permission: 'leads:read',
  },
  {
    id: 'go-dashboard',
    group: 'navigate',
    labelKey: 'shortcutGoDashboard',
    mnemonicKey: 'shortcutMnemonicGoDashboard',
    chord: { kind: 'sequence', keys: ['g', 'h'] },
    run: { type: 'navigate', path: '/dashboard' },
  },
  {
    id: 'go-my-tasks',
    group: 'navigate',
    labelKey: 'shortcutGoMyTasks',
    mnemonicKey: 'shortcutMnemonicGoMyTasks',
    chord: { kind: 'sequence', keys: ['g', 't'] },
    run: { type: 'navigate', path: '/tasks?view=list&mine=1' },
    permission: 'task:read',
  },
  {
    id: 'go-messaging',
    group: 'navigate',
    labelKey: 'shortcutGoMessaging',
    mnemonicKey: 'shortcutMnemonicGoMessaging',
    chord: { kind: 'sequence', keys: ['g', 'm'] },
    run: { type: 'navigate', path: '/messaging' },
    permission: 'messaging:read',
  },
  {
    id: 'create-appointment',
    group: 'create',
    labelKey: 'spotlightCreateAppointment',
    mnemonicKey: 'shortcutMnemonicCreateAppointment',
    chord: { kind: 'sequence', keys: ['c', 'a'] },
    run: { type: 'open-create', actionId: 'create-appointment' },
    permission: 'appointments:create',
  },
  {
    id: 'create-patient',
    group: 'create',
    labelKey: 'spotlightCreatePatient',
    mnemonicKey: 'shortcutMnemonicCreatePatient',
    chord: { kind: 'sequence', keys: ['c', 'p'] },
    run: { type: 'open-create', actionId: 'create-patient' },
    permission: 'patients:create',
  },
  {
    id: 'create-lead',
    group: 'create',
    labelKey: 'spotlightCreateLead',
    mnemonicKey: 'shortcutMnemonicCreateLead',
    chord: { kind: 'sequence', keys: ['c', 'l'] },
    run: { type: 'open-create', actionId: 'create-lead' },
    permission: 'leads:create',
  },
  {
    id: 'create-task',
    group: 'create',
    labelKey: 'spotlightCreateTask',
    mnemonicKey: 'shortcutMnemonicCreateTask',
    chord: { kind: 'sequence', keys: ['c', 't'] },
    run: {
      type: 'open-create',
      actionId: 'create-task',
      path: '/tasks?view=list&mine=1&create=1',
    },
    permission: 'task:create',
  },
];

export const shortcutAllowed = (
  shortcut: ShortcutDef,
  hasPermission: (permission: string) => boolean,
  isAdmin: boolean,
): boolean => {
  if (isAdmin) return true;
  if (!shortcut.permission) return true;
  return hasPermission(shortcut.permission);
};

export const visibleShortcuts = (
  hasPermission: (permission: string) => boolean,
  isAdmin: boolean,
): ShortcutDef[] =>
  KEYBOARD_SHORTCUTS.filter((s) => shortcutAllowed(s, hasPermission, isAdmin));
